import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { AlertTriangle, Calendar, Clock, CloudRain, Mail, Megaphone, Radio, Shield, ThermometerSun, Users, Wind, Wrench, Waves, PowerOff, Sparkles } from 'lucide-react';
import TwilioTestSmsCard from './TwilioTestSmsCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Pre-written emergency templates — CEO can pick one, tweak, and send.
// Slashes team-typing time from minutes to seconds during a real blackout.
const EMERGENCY_TEMPLATES = [
  {
    key: 'winter_weather',
    label: 'Winter Weather Blackout',
    icon: CloudRain,
    audience: 'both',
    subject: 'Winter Weather · Service Paused',
    message: 'Valet trash service is paused today due to severe winter weather. Please keep bags inside — pickup will resume when roads clear. Stay safe.',
  },
  {
    key: 'severe_storm',
    label: 'Severe Storm Warning',
    icon: CloudRain,
    audience: 'both',
    subject: 'Severe Storm · Service Suspended',
    message: 'Severe storm warning in your area. Valet trash pickup is suspended tonight for staff safety. Service resumes tomorrow evening.',
  },
  {
    key: 'system_maintenance',
    label: 'System Maintenance Window',
    icon: Wrench,
    audience: 'staff',
    subject: 'App Maintenance · 60 min',
    message: 'The NECS staff app will be offline for maintenance from 2:00–3:00 AM tonight. Complete any pending scans before then.',
  },
  {
    key: 'route_detour',
    label: 'Route Detour · Road Closure',
    icon: Radio,
    audience: 'staff',
    subject: 'Route Detour Required',
    message: 'A road closure is affecting tonight’s route. Please check your app for the updated turn-by-turn — expect a 15-minute delay.',
  },
  {
    key: 'water_shutoff',
    label: 'Water Shut-off Notice',
    icon: Waves,
    audience: 'residents',
    subject: 'Property Water Shut-off',
    message: 'The property will have a scheduled water shut-off from 9:00 AM – 1:00 PM tomorrow. Please plan accordingly.',
  },
  {
    key: 'power_outage',
    label: 'Power Outage Notice',
    icon: PowerOff,
    audience: 'both',
    subject: 'Power Outage · Service Impact',
    message: 'A power outage is affecting the region. Service may be delayed until utilities are restored. Updates to follow.',
  },
];

/**
 * Broadcast panel — operates in one of two modes:
 *
 *   • SMS (Blackout / Emergency) — restricted to Admin / Ops Admin / HR Admin.
 *   • Regional (Push + Email)    — Supervisors + Crew Leaders + Admins.
 *
 * The `mode` prop picks which endpoint gets called and rebrands the copy;
 * when omitted it auto-selects based on the user's role.
 */
export default function EmergencyBroadcastPanel({ user, regions = [], mode }) {
  const role = user?.role;
  const SMS_ROLES = ['admin', 'company_manager', 'ops_admin', 'hr_admin'];
  const canSmsBroadcast = SMS_ROLES.includes(role);
  const isSupervisor = role === 'supervisor' || role === 'crew_leader';
  const effectiveMode = mode || (canSmsBroadcast ? 'sms' : 'regional');
  const canBroadcast = canSmsBroadcast || isSupervisor;
  const isSms = effectiveMode === 'sms';

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [audience, setAudience] = useState('both');
  const [regionCode, setRegionCode] = useState(isSupervisor ? (user?.region || '') : '');
  const [includeEmail, setIncludeEmail] = useState(true);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  // Weather intelligence + scheduling (Feb 2026)
  const [weather, setWeather] = useState(null);
  const [loadingWeather, setLoadingWeather] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleAt, setScheduleAt] = useState(''); // datetime-local value

  const token = useMemo(() => localStorage.getItem('session_token') || '', []);
  const authHeaders = useMemo(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const refreshPreview = async () => {
    if (!canBroadcast) return;
    setLoadingPreview(true);
    try {
      const q = new URLSearchParams({ audience });
      if (!isSupervisor && regionCode) q.set('region_code', regionCode);
      const res = await fetch(`${BACKEND_URL}/api/broadcasts/preview?${q.toString()}`, {
        headers: authHeaders,
      });
      const data = await res.json();
      if (res.ok) setPreview(data); else setPreview(null);
    } catch (e) {
      console.error('[broadcast preview]', e);
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/broadcasts`, { headers: authHeaders });
      const data = await res.json();
      if (res.ok) setHistory(data.broadcasts || []);
    } catch (e) {
      console.error('[broadcast history]', e);
    }
  };

  useEffect(() => {
    refreshPreview();
    loadHistory();
  }, [audience, regionCode]);

  // Fetch weather forecast whenever the selected region changes. Runs in
  // SMS mode only — Regional Update panel doesn't need weather intel.
  useEffect(() => {
    if (!isSms) { setWeather(null); return; }
    let cancelled = false;
    (async () => {
      setLoadingWeather(true);
      try {
        const q = new URLSearchParams();
        if (regionCode) q.set('region_code', regionCode);
        const res = await fetch(`${BACKEND_URL}/api/weather/region?${q.toString()}`, {
          headers: authHeaders,
        });
        const data = await res.json();
        if (!cancelled && res.ok) setWeather(data);
      } catch (e) {
        console.error('[weather]', e);
      } finally {
        if (!cancelled) setLoadingWeather(false);
      }
    })();
    return () => { cancelled = true; };
  }, [regionCode, isSms]);

  const applyWeatherSuggestion = () => {
    const key = weather?.suggestion?.template_key;
    const templ = EMERGENCY_TEMPLATES.find((t) => t.key === key);
    if (!templ) return;
    setSubject(templ.subject);
    setMessage(`${templ.message}\n\nForecast: ${weather.suggestion.reason}`);
    setAudience(templ.audience);
    toast.success('Weather-suggested template loaded — review before sending.');
  };

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    setSending(true);
    try {
      const path = effectiveMode === 'sms' ? '/api/broadcasts/emergency' : '/api/broadcasts/regional';
      const body = {
        subject: subject.trim(),
        message: message.trim(),
        audience,
        include_email: includeEmail,
      };
      if (effectiveMode === 'sms') body.confirm = true;
      if (effectiveMode === 'sms' && scheduleEnabled && scheduleAt) {
        // Convert datetime-local (assumed to be user's local time) to UTC ISO.
        body.scheduled_for = new Date(scheduleAt).toISOString();
      }
      if (!isSupervisor && regionCode) body.region_code = regionCode;

      const res = await fetch(`${BACKEND_URL}${path}`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Broadcast failed');
        return;
      }
      const c = data?.broadcast?.recipient_counts || {};
      if (effectiveMode === 'sms') {
        if (data?.queued) {
          toast.success(
            `Broadcast queued for ${new Date(data.broadcast.scheduled_for).toLocaleString()} · ${c.total} recipients`
          );
        } else if (data?.queued_background) {
          toast.success(`Broadcast dispatching to ${c.total} recipients (running in background)`);
        } else {
          const s = data?.broadcast?.sms_result || {};
          const testNote = s.test_mode ? ' (test-mode — SMS logged, not sent)' : '';
          toast.success(`Broadcast dispatched to ${c.total} recipients · SMS sent: ${s.sent}${testNote}`);
        }
      } else {
        toast.success(
          `Regional update sent · ${data.broadcast?.push_sent || 0} push · ${data.broadcast?.email_sent || 0} email`
        );
      }
      setConfirmOpen(false);
      setSubject('');
      setMessage('');
      loadHistory();
    } catch (e) {
      console.error('[broadcast send]', e);
      toast.error('Broadcast failed — please retry');
    } finally {
      setSending(false);
    }
  };

  if (!canBroadcast) return null;

  const staffCount = preview?.staff?.total || 0;
  const residentCount = preview?.residents?.total || 0;
  const staffPhones = preview?.staff?.with_phone || 0;
  const residentPhones = preview?.residents?.with_phone || 0;
  const totalPhones = (audience === 'residents' ? 0 : staffPhones)
    + (audience === 'staff' ? 0 : residentPhones);
  const totalRecipients = (audience === 'residents' ? 0 : staffCount)
    + (audience === 'staff' ? 0 : residentCount);
  const sendDisabledCount = effectiveMode === 'sms' ? totalPhones : totalRecipients;

  const themeBase = isSms
    ? 'border-red-800/40 bg-gradient-to-br from-red-950/50 to-black'
    : 'border-amber-700/40 bg-gradient-to-br from-amber-950/40 to-black';
  const themeText = isSms ? 'text-red-200' : 'text-amber-200';
  const themeMuted = isSms ? 'text-red-300/80' : 'text-amber-300/80';
  const btnClass = isSms ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700';

  return (
    <div className="space-y-4" data-testid="emergency-broadcast-panel" data-mode={effectiveMode}>
      {/* CEO/Admin-only Twilio deliverability check — mounted at the top of
          the panel so it's the first thing you see when your A2P campaign
          finally gets the green light. */}
      {(user?.role === 'admin' || user?.role === 'company_manager') && (
        <TwilioTestSmsCard user={user} />
      )}
      <Card className={themeBase}>
        <CardHeader className="flex flex-row items-start gap-3 pb-3">
          {isSms ? (
            <AlertTriangle className="h-6 w-6 text-red-400 shrink-0 mt-1" />
          ) : (
            <Radio className="h-6 w-6 text-amber-400 shrink-0 mt-1" />
          )}
          <div>
            <CardTitle className={`${themeText} flex items-center gap-2`}>
              {isSms ? 'Emergency Ops · Blackout SMS' : 'Regional Update · Push + Email'}
            </CardTitle>
            <p className={`text-xs ${themeMuted} mt-1`}>
              {isSms
                ? 'Sends a critical SMS to every recipient in scope. Twilio charges per message — a two-step confirm is required.'
                : 'Sends a Push notification + Email to every recipient in scope. No SMS — cost-controlled channel.'}
            </p>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isSms && (
            <div data-testid="broadcast-templates">
              <Label className={`text-xs uppercase tracking-wider ${themeText} flex items-center gap-1.5`}>
                <Sparkles className="h-3 w-3" /> Quick templates
              </Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {EMERGENCY_TEMPLATES.map(({ key, label, icon: Icon, audience: templAudience, subject: s, message: m }) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`broadcast-template-${key}`}
                    onClick={() => {
                      setSubject(s);
                      setMessage(m);
                      setAudience(templAudience);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-red-900/40 bg-black/30 text-red-100 hover:bg-red-950 hover:border-red-700 transition"
                    title={`${s} · audience: ${templAudience}`}
                  >
                    <Icon className="h-3 w-3" /> {label}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-red-300/60 mt-1.5">
                Click a template to prefill Subject + Message + Audience. You can still tweak before confirming.
              </p>
            </div>
          )}

          {isSms && (
            <WeatherIntelBox
              weather={weather}
              loading={loadingWeather}
              onApplySuggestion={applyWeatherSuggestion}
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className={`text-xs uppercase tracking-wider ${themeText}`}>Subject</Label>
              <Input
                data-testid="broadcast-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={isSms ? 'Weather emergency · service blackout · outage…' : 'Schedule change · morning briefing · route update…'}
                maxLength={120}
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className={`text-xs uppercase tracking-wider ${themeText}`}>Audience</Label>
              <div className="flex gap-2 mt-1" data-testid="broadcast-audience-toggle">
                {[
                  { key: 'staff', label: 'Staff only', icon: Shield },
                  { key: 'residents', label: 'Residents only', icon: Users },
                  { key: 'both', label: 'Both', icon: Radio },
                ].map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`broadcast-audience-${key}`}
                    onClick={() => setAudience(key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition ${
                      audience === key
                        ? `${btnClass} text-white border-transparent`
                        : 'bg-black/40 text-white/80 border-white/10 hover:bg-white/5'
                    }`}
                  >
                    <Icon className="h-3 w-3" /> {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {!isSupervisor && (
            <div>
              <Label className={`text-xs uppercase tracking-wider ${themeText}`}>Region scope</Label>
              <select
                data-testid="broadcast-region-select"
                value={regionCode}
                onChange={(e) => setRegionCode(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm"
              >
                <option value="" data-testid="broadcast-region-all">Company-wide (all regions)</option>
                {regions.map((r) => (
                  <option key={r.region_id || r.region_code || r.code} value={r.region_code || r.code} data-testid={`broadcast-region-${r.region_id || r.region_code || r.code}`}>
                    {`${r.region_code || r.code || ''} — ${r.name || r.region_name || ''}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {isSupervisor && (
            <div className={`text-xs ${themeText} bg-black/30 border border-white/10 rounded px-3 py-2`} data-testid="broadcast-supervisor-scope-notice">
              Your update goes to your assigned region only ({user?.region || 'auto'}). SMS is
              disabled for Supervisors + Crew Leaders — Admin / Ops handle company-wide SMS.
            </div>
          )}

          <div>
            <Label className={`text-xs uppercase tracking-wider ${themeText}`}>Message body</Label>
            <Textarea
              data-testid="broadcast-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={isSms
                ? 'Service is paused in your area until 6:00 PM due to severe weather. Stay indoors.'
                : 'Tomorrow start time moved to 6:00 AM. Check your route in the app.'}
              rows={3}
              maxLength={isSms ? 480 : 1000}
              className="bg-black/40 border-white/10 text-white"
            />
            <div className={`text-[10px] ${themeMuted} mt-1`}>
              {message.length}/{isSms ? 480 : 1000} chars{isSms ? ' · SMS may split into multiple segments over 160 chars.' : ''}
            </div>
          </div>

          <label className={`flex items-center gap-2 text-xs ${themeText}`}>
            <input
              type="checkbox"
              data-testid="broadcast-include-email"
              checked={includeEmail}
              onChange={(e) => setIncludeEmail(e.target.checked)}
            />
            {isSms ? 'Also send an email backup (recommended for weather blackouts)' : 'Also send an email (recommended)'}
          </label>

          {isSms && (
            <div className="border border-red-900/40 rounded p-3 bg-black/20 space-y-2" data-testid="broadcast-schedule-box">
              <label className={`flex items-center gap-2 text-xs ${themeText} font-semibold`}>
                <input
                  type="checkbox"
                  data-testid="broadcast-schedule-toggle"
                  checked={scheduleEnabled}
                  onChange={(e) => setScheduleEnabled(e.target.checked)}
                />
                <Calendar className="h-3.5 w-3.5" /> Schedule for later
              </label>
              {scheduleEnabled && (
                <div className="flex items-center gap-2 pl-6">
                  <Clock className="h-3.5 w-3.5 text-red-300" />
                  <Input
                    type="datetime-local"
                    data-testid="broadcast-schedule-datetime"
                    value={scheduleAt}
                    onChange={(e) => setScheduleAt(e.target.value)}
                    min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                    className="bg-black/40 border-red-900/40 text-white text-xs w-56"
                  />
                  <span className="text-[10px] text-red-300/70">
                    Queues the announcement for automatic delivery at this time.
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between border-t border-white/10 pt-3">
            <div className={`text-xs ${themeText}`} data-testid="broadcast-preview-counts">
              {loadingPreview ? (
                <span className="italic opacity-60">Loading preview…</span>
              ) : preview ? (
                <>
                  <b>{isSms ? totalPhones : totalRecipients}</b> {isSms ? 'phones' : 'recipients'} ·{' '}
                  <Badge className="ml-1 bg-black/40 border border-white/10 text-white">
                    {staffCount} staff
                  </Badge>{' '}
                  <Badge className="ml-1 bg-black/40 border border-white/10 text-white">
                    {residentCount} residents
                  </Badge>
                </>
              ) : (
                <span className="opacity-60">No preview data</span>
              )}
            </div>
            <Button
              data-testid="broadcast-open-confirm"
              onClick={() => (isSms ? setConfirmOpen(true) : handleSend())}
              disabled={!subject.trim() || !message.trim() || sendDisabledCount === 0 || sending}
              className={`${btnClass} text-white gap-2`}
            >
              {isSms ? <Megaphone className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
              {isSms
                ? (scheduleEnabled && scheduleAt ? 'Schedule Blackout SMS' : 'Send Blackout SMS')
                : (sending ? 'Sending…' : 'Send Regional Update')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {history.length > 0 && (
        <Card className="bg-black/40 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className={`${themeText} text-sm`}>Recent broadcasts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-xs" data-testid="broadcast-history">
            {history.slice(0, 8).map((b) => (
              <div
                key={b.broadcast_id}
                className="flex items-start justify-between gap-3 border-b border-white/5 pb-2 last:border-0"
                data-testid={`broadcast-row-${b.broadcast_id}`}
              >
                <div>
                  <div className="text-white font-medium">{b.subject}</div>
                  <div className="text-white/60">{b.message}</div>
                  <div className="text-white/40 mt-0.5">
                    {new Date(b.created_at).toLocaleString()} · {b.audience} ·{' '}
                    {b.region_code || 'company-wide'} · by {b.triggered_by_name}
                    {b.channel === 'push+email' ? ' · push+email' : ' · SMS'}
                  </div>
                </div>
                <div className="text-right shrink-0 text-white/80">
                  <div className="font-semibold">
                    {b.recipient_counts?.total || 0} rcpts
                  </div>
                  <div className="text-[10px]">
                    {b.sms_result?.sent != null
                      ? `${b.sms_result.sent} SMS${b.sms_result.test_mode ? ' · test' : ''}`
                      : `${b.push_sent || 0} push · ${b.email_sent || 0} email`}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="broadcast-confirm-dialog" className="bg-zinc-950 border-red-800/40">
          <DialogHeader>
            <DialogTitle className="text-red-200 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" /> Confirm emergency broadcast
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-red-100">
            <p>
              You&apos;re about to send <b>{totalPhones}</b> SMS messages (
              <b>{audience}</b>{regionCode ? ` · ${regionCode}` : ' · company-wide'}).
              Twilio charges per message.
            </p>
            <div className="bg-black/40 border border-red-900/40 rounded px-3 py-2">
              <div className="font-medium">{subject}</div>
              <div className="text-red-300/80 whitespace-pre-wrap">{message}</div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              data-testid="broadcast-cancel"
              onClick={() => setConfirmOpen(false)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              data-testid="broadcast-confirm-send"
              onClick={handleSend}
              disabled={sending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {sending ? 'Sending…' : `Confirm & Send`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * WeatherIntelBox — 24-hour forecast summary + auto-suggested template.
 *
 * Renders inside the SMS-mode Emergency Ops panel so an Admin/Ops user can
 * see, at a glance, whether tonight's shift is likely to face freeze, heavy
 * rain, high winds, or a heat wave — with a one-click "Apply suggestion"
 * that prefills the broadcast template.
 */
function WeatherIntelBox({ weather, loading, onApplySuggestion }) {
  if (loading) {
    return (
      <div
        className="border border-red-900/40 rounded p-3 bg-black/20 text-xs text-red-300/70"
        data-testid="broadcast-weather-loading"
      >
        Loading weather forecast…
      </div>
    );
  }
  if (!weather) return null;
  const s = weather.summary_24h || {};
  const cur = weather.current || {};
  const sug = weather.suggestion;
  return (
    <div
      className="border border-red-900/40 rounded-lg p-3 bg-black/30 space-y-2"
      data-testid="broadcast-weather-box"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-red-200 font-semibold">
          <CloudRain className="h-3.5 w-3.5" /> 24-hour forecast
          <span className="normal-case text-red-300/70 tracking-normal font-normal">
            · {weather.region_name || weather.region_code || 'region'} ({weather.state_or_province || '—'})
          </span>
        </div>
        {typeof cur.temperature === 'number' && (
          <div className="text-[11px] text-red-300/80" data-testid="broadcast-weather-current">
            Now · {cur.temperature.toFixed(1)}°C · wind {cur.windspeed?.toFixed?.(0) ?? cur.windspeed} km/h
          </div>
        )}
      </div>
      <div className="grid grid-cols-4 gap-2 text-[11px]">
        <WxStat icon={ThermometerSun} label="Min °C" value={s.min_temp_c} testid="wx-min-temp" tone="cool" />
        <WxStat icon={ThermometerSun} label="Max °C" value={s.max_temp_c} testid="wx-max-temp" tone="warm" />
        <WxStat icon={CloudRain} label="Peak rain mm" value={s.max_precip_mm} testid="wx-max-rain" tone="rain" />
        <WxStat icon={Wind} label="Peak wind km/h" value={s.max_wind_kmh} testid="wx-max-wind" tone="wind" />
      </div>
      {sug ? (
        <div
          className="rounded border border-amber-600/50 bg-amber-950/30 px-3 py-2 flex items-start gap-2"
          data-testid="broadcast-weather-suggestion"
        >
          <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-xs font-semibold text-amber-200">
              Suggested template · {sug.template_key.replace(/_/g, ' ')}
            </div>
            <div className="text-[11px] text-amber-100/80">{sug.reason}</div>
          </div>
          <button
            type="button"
            data-testid="broadcast-weather-apply"
            onClick={onApplySuggestion}
            className="px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-400 text-black text-[11px] font-semibold whitespace-nowrap"
          >
            Apply
          </button>
        </div>
      ) : (
        <div className="text-[11px] text-red-300/60 italic" data-testid="broadcast-weather-clear">
          No emergency thresholds crossed in the next 24 hours — conditions look normal.
        </div>
      )}
    </div>
  );
}

function WxStat({ icon: Icon, label, value, tone, testid }) {
  const toneClass = {
    cool: 'text-sky-300',
    warm: 'text-amber-300',
    rain: 'text-blue-300',
    wind: 'text-emerald-300',
  }[tone] || 'text-red-200';
  const display = value == null ? '—' : Number(value).toFixed(1);
  return (
    <div
      className="bg-black/30 border border-white/5 rounded px-2 py-1.5"
      data-testid={testid}
    >
      <div className={`flex items-center gap-1 ${toneClass}`}>
        <Icon className="h-3 w-3" />
        <span className="font-semibold">{display}</span>
      </div>
      <div className="text-[9px] uppercase tracking-wider text-white/50">{label}</div>
    </div>
  );
}

